import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_ROLES = ['Contractor', 'Estimator', 'Labor', 'Marketing', 'Office', 'Production', 'Project Lead', 'Time Approver'];
const ALL_FRANCHISE = [
  'Langhorne/Bensalem (10089)',
  'Fox Chase/Torresdale (10090)',
  'Society Hill (11669)',
  'Burlington Township/Mount Holly (11670)',
  'Downtown Philadelphia (19032)'
];
const ALL_APP_ACCESS = ['Create Records', 'Update Records', 'Delete Records', 'Setup Options', 'Management Options'];

const CreateUserProfile = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const [inactive, setInactive] = useState(false);
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [payRate, setPayRate] = useState('');
  const [bonusPct, setBonusPct] = useState('');
  const [roles, setRoles] = useState([]);
  const [franchiseAccess, setFranchiseAccess] = useState([]);
  const [appAccess, setAppAccess] = useState([]);
  const [commissionManagement, setCommissionManagement] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);

  const toggleItem = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const handleSave = async () => {
    // Re-entrancy guard: prevents a rapid double-click from posting twice.
    if (savingRef.current) return;
    if (!lastName || !firstName || !userEmail) {
      toast.error('Last Name, First Name, and Email are required');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const body = {
        last_name: lastName,
        first_name: firstName,
        email: userEmail,
        active: !inactive,
        pay_rate: parseFloat(payRate) || 0,
        bonus_pct: parseFloat(bonusPct) || 0,
        roles,
        franchise_access: franchiseAccess,
        can_create: appAccess.includes('Create Records') ? 'Yes' : 'No',
        can_update: appAccess.includes('Update Records') ? 'Yes' : 'No',
        can_delete: appAccess.includes('Delete Records') ? 'Yes' : 'No',
        setup: appAccess.includes('Setup Options') ? 'Yes' : 'No',
        management: appAccess.includes('Management Options') ? 'Yes' : 'No',
        commission_management: commissionManagement,
        send_welcome: sendWelcome
      };
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Create failed');
      const { data } = await readResponse(res);
      if (data.welcome_skipped) {
        toast.success('User created. No welcome email was sent.');
      } else if (data.email_sent === false) {
        toast.warning(`User created, but welcome email failed: ${data.email_error || 'unknown error'}. You can resend it from the user's profile.`);
      } else {
        toast.success('User created successfully. Welcome email sent.');
      }
      navigate('/setup/users-employees');
    } catch {
      toast.error('Failed to create user');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Create New User</h1>
        </div>

        <div className="mb-6">
          <button
            onClick={() => navigate('/setup/users-employees')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
            data-testid="back-btn"
          >
            Back
          </button>
        </div>

        <div className="bg-white rounded shadow p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* User Information */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">User Information</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="text-gray-700 text-sm w-24 text-left">Inactive:</label>
                  <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} className="w-4 h-4" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">Last Name:</label>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">First Name:</label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">Email:</label>
                  <input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm w-24 text-right">Pay Rate:</label>
                  <input type="number" value={payRate} onChange={(e) => setPayRate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-20" />
                  <span className="text-sm text-gray-500">per hr</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm w-24 text-right">Bonus %:</label>
                  <input type="number" value={bonusPct} onChange={(e) => setBonusPct(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-20" />
                  <span className="text-sm text-gray-500">% (0-100)</span>
                </div>
                <p className="text-xs text-gray-400 ml-28">Percent of value minus expenses and commission</p>
                <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
                  <input
                    type="checkbox"
                    checked={sendWelcome}
                    onChange={(e) => setSendWelcome(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-[#4ECFDB]"
                    data-testid="send-welcome-checkbox"
                  />
                  <label className="text-sm text-gray-700 cursor-pointer" onClick={() => setSendWelcome(v => !v)}>
                    Send welcome email
                    <span className="block text-xs text-gray-400">
                      Uncheck for techs/labor who only need time &amp; expense tracking and won't log in.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Roles */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Roles</h2>
              <div className="space-y-3">
                {ALL_ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={roles.includes(role)} onChange={() => toggleItem(roles, setRoles, role)} className="w-4 h-4" />
                    {role}
                  </label>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Certifications</h2>
              <p className="text-sm text-gray-400 italic">No certifications configured</p>
            </div>

            {/* Franchise Access */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Franchise Access</h2>
              <div className="space-y-3">
                {ALL_FRANCHISE.map(loc => (
                  <label key={loc} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={franchiseAccess.includes(loc)} onChange={() => toggleItem(franchiseAccess, setFranchiseAccess, loc)} className="w-4 h-4" />
                    {loc}
                  </label>
                ))}
              </div>
            </div>

            {/* Application Access */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Application Access</h2>
              <div className="space-y-3">
                {ALL_APP_ACCESS.map(access => (
                  <label key={access} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={appAccess.includes(access)} onChange={() => toggleItem(appAccess, setAppAccess, access)} className="w-4 h-4" />
                    {access}
                  </label>
                ))}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={commissionManagement} onChange={(e) => setCommissionManagement(e.target.checked)} className="w-4 h-4" />
                  Commission Management
                </label>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors border border-[#4ECFDB] disabled:opacity-50"
              data-testid="save-user-btn"
            >
              {saving ? 'Saving...' : 'Save User'}
            </button>
          </div>

          <p className="mt-4 text-sm">
            <span className="text-red-500 font-semibold">Note: </span>
            <span className="text-red-400">
              {sendWelcome
                ? 'New users will receive a welcome email with a link to create a password.'
                : 'No welcome email will be sent. You can send one later from the user\u2019s profile if they ever need to log in.'}
            </span>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default CreateUserProfile;
